import { colors, icons, intl } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode, Ref } from 'react'

const BOX_ID = 'presenter-settings-box'
const HEADING_ID = 'presenter-settings-title'

/**
 * The gear and its box hang off the head row of the panel: the box is
 * positioned against that row (`position: relative` there), so it opens right
 * under the gear and spans the panel, not just the gear's own width.
 */
const Anchor = styled.div`
	display: flex;
	flex: none;
	align-self: stretch;
`

const Gear = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	padding: 0;
	border: none;
	border-radius: 10px;
	background: ${colors.paleGray};
	color: ${colors.darkIndigo};
	cursor: pointer;

	:hover,
	&[aria-expanded='true'] {
		background: ${colors.indigoBlue10};
	}

	:focus-visible {
		outline: 2px solid ${colors.indigoBlue};
		outline-offset: 1px;
	}

	svg {
		width: 20px;
		height: 20px;
	}
`

/** The small warning on the gear's corner: something inside needs the trainer. */
const Dot = styled.span`
	position: absolute;
	top: -3px;
	right: -3px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: ${colors.dustyOrange};
	box-shadow: 0 0 0 2px ${colors.white};
`

/** Read by assistive tech, never shown. */
const Announcement = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`

const Box = styled.div`
	position: absolute;
	z-index: 2;
	top: calc(100% + 8px);
	right: -12px;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 320px;
	max-width: calc(100% + 24px);
	max-height: 70vh;
	padding: 16px;
	overflow-y: auto;
	background: ${colors.white};
	border: 1px solid ${colors.borderLightGray};
	border-radius: 16px;
	box-shadow: 0 6px 24px ${colors.black15};
`

const Heading = styled.h3`
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	color: ${colors.darkIndigo};
`

type Props = {
	open: boolean
	onToggle: () => void
	/** On the gear, for focus to return to. */
	triggerRef: Ref<HTMLButtonElement>
	/** On the open box, for what counts as inside and for focus to go into. */
	boxRef: Ref<HTMLDivElement>
	/**
	 * What a screen reader hears while something in the box needs the trainer;
	 * `null` while nothing does. It also puts the dot on the gear.
	 */
	attention: string | null
	/** The settings, shown in the box while it is open. */
	children: ReactNode
}

/**
 * The presenter panel's settings: a gear that opens a small box beside it,
 * not a modal - the trainer keeps the panel and the stage in reach while it
 * is open. Whatever needs the trainer's notice shows on the gear even when
 * the box is closed.
 */
export const PresenterSettings: FC<Props> = ({
	open,
	onToggle,
	triggerRef,
	boxRef,
	attention,
	children,
}) => {
	const translate = intl.useTranslate()
	const label = translate(
		attention === null
			? 'in_person.presenter.settings'
			: 'in_person.presenter.settings.attention'
	)

	return (
		<Anchor>
			<Gear
				ref={triggerRef}
				type="button"
				data-testid="presenter.settings"
				aria-label={label}
				title={label}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-controls={BOX_ID}
				onClick={onToggle}
			>
				<icons.SettingsIcon />
				{attention === null ? null : (
					<Dot aria-hidden data-testid="presenter.settings.attention" />
				)}
			</Gear>
			<Announcement role="status" data-testid="presenter.settings.status">
				{attention}
			</Announcement>
			{open ? (
				<Box
					ref={boxRef}
					id={BOX_ID}
					role="dialog"
					aria-labelledby={HEADING_ID}
					tabIndex={-1}
					data-testid="presenter.settings.box"
				>
					<Heading id={HEADING_ID}>
						<intl.Translate>in_person.presenter.settings.title</intl.Translate>
					</Heading>
					{children}
				</Box>
			) : null}
		</Anchor>
	)
}
